import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ip_back } from "../config";
import {
  parseTableNumberFromVideoUrl,
} from "../utils/evaluationVideoMatching";
import { parseKotMatchIdFromFeedbackText } from "../utils/kotPlayerMatches";
import {
  geometryModeFromSetup,
  normalizeCameraSetup,
  parseCameraSetupFromFilename,
} from "../utils/evaluationCameraSetup";

function authHeaders() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function isActiveSlotRole(role) {
  const r = String(role || "").toLowerCase();
  if (!r) return false;
  if (r === "both") return true;
  return (
    r === "active" ||
    r.includes("actif") ||
    r.includes("activo") ||
    r.includes("aktiv")
  );
}

function handLabel(hand, t) {
  const h = String(hand || "").toLowerCase();
  if (h === "left") return t("leftHand", "Left-Handed");
  if (h === "right") return t("rightHand", "Right-Handed");
  return null;
}

function formatSlotOptionLabel(slot, t) {
  const hand = handLabel(slot.player_hand, t);
  const handSuffix = hand ? ` (${hand})` : "";
  const roleRaw = String(slot.role || "").trim();
  const roleLabel = roleRaw
    ? roleRaw.charAt(0).toUpperCase() + roleRaw.slice(1).toLowerCase()
    : "";
  const roleSuffix = roleLabel ? ` - ${roleLabel}` : "";
  return `${slot.position}: ${slot.display_name}${roleSuffix}${handSuffix}`;
}

function formatKotPlayerLabel(player, side, t) {
  if (!player) {
    return `${side}: ${t("unknownPlayer", "Unknown player")}`;
  }
  const hand = handLabel(player.player_hand, t);
  const handSuffix = hand ? ` (${hand})` : "";
  return `${side}: ${player.display_name || player.player_id}${handSuffix}`;
}

function formatExerciseOption(ex, t, { multiStation = false } = {}) {
  const order = ex.te_order != null ? `#${ex.te_order}` : "#?";
  const name = ex.name || ex.te_id;
  const stationName = multiStation
    ? ex.station_is_main
      ? t("mainGroup", "Main group")
      : ex.station_name || ""
    : "";
  const stationPrefix = stationName ? `${stationName} · ` : "";
  if (ex.is_kot_type) {
    return `${stationPrefix}${order} · KoT · ${name}`;
  }
  const tables = Array.isArray(ex.tables)
    ? ex.tables.map(Number).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  if (tables.length === 1) {
    const tableLabel = t("table_with_number", {
      number: tables[0],
      defaultValue: `Table ${tables[0]}`,
    });
    return `${stationPrefix}${order} · ${tableLabel} · ${name}`;
  }
  return `${stationPrefix}${order} · ${name}`;
}

function formatSequenceOption(seq, t, locale) {
  if (seq.sequence_order == null) {
    return t("noSequence", "No sequence");
  }
  const loc =
    locale === "fr"
      ? "sub_type_name_fr"
      : locale === "de"
        ? "sub_type_name_de"
        : locale === "es"
          ? "sub_type_name_es"
          : locale === "nl"
            ? "sub_type_name_nl"
            : locale === "pt"
              ? "sub_type_name_pt"
              : "sub_type_name_en";
  const name =
    seq[loc] ||
    seq.sub_type_name_en ||
    seq.sub_type_name_fr ||
    seq.name ||
    "";
  return name
    ? `${seq.sequence_order} · ${name}`
    : String(seq.sequence_order);
}

/** Tables that actually have players/slots on this exercise — never session-wide fallback. */
function tableOptionsForExercise(ex) {
  if (ex?.is_kot_type) {
    const fromMatches = new Set();
    for (const m of ex.kot_matches || []) {
      const n = Number(m.table_number);
      if (Number.isFinite(n) && n > 0) fromMatches.add(n);
    }
    if (fromMatches.size) return Array.from(fromMatches).sort((a, b) => a - b);
  }

  const fromSlots = new Set();
  for (const seq of ex?.sequences || []) {
    for (const slot of seq.slots || []) {
      const n = Number(slot.table_number);
      if (Number.isFinite(n) && n > 0) fromSlots.add(n);
    }
    const seqTable = Number(seq.table_number);
    if (Number.isFinite(seqTable) && seqTable > 0) fromSlots.add(seqTable);
  }
  if (fromSlots.size) return Array.from(fromSlots).sort((a, b) => a - b);

  const fromEx = Array.isArray(ex?.tables)
    ? ex.tables.map(Number).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  if (fromEx.length) return [...new Set(fromEx)].sort((a, b) => a - b);

  const fallback = Number(ex?.table_number);
  return Number.isFinite(fallback) && fallback > 0 ? [fallback] : [];
}

function parseViewSetupTableNumber(message) {
  const raw = message?.view_setup;
  if (raw == null || raw === "") return null;
  try {
    const setup = typeof raw === "string" ? JSON.parse(raw) : raw;
    const n = Number(setup?.table_number);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function cameraViewFromMessage(message) {
  let vs = message?.view_setup;
  if (typeof vs === "string") {
    try {
      vs = JSON.parse(vs);
    } catch {
      vs = null;
    }
  }
  const explicit = normalizeCameraSetup(
    vs?.camera_setup ||
      vs?.cameraSetup ||
      vs?.camera_setup_id ||
      (vs?.user && (vs.user.camera_setup || vs.user.cameraSetup))
  );
  if (explicit) return geometryModeFromSetup(explicit);
  const fromFile = parseCameraSetupFromFilename(message?.video_message_url);
  if (fromFile) return geometryModeFromSetup(fromFile);
  return "spectator";
}

/**
 * Coach dialog to reassign an evaluation video to another exercise / sequence / table / side,
 * or for KoT: round / table / match players.
 */
export function MoveEvaluationVideoDialog({
  open,
  onClose,
  message,
  tsId,
  currentTeId,
  onMoved,
  onDeleted,
  zIndex = 15000,
}) {
  const { t, i18n } = useTranslation();
  const localeCode = (i18n.language || "en").slice(0, 2);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [teId, setTeId] = useState("");
  const [sequenceNumber, setSequenceNumber] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [position, setPosition] = useState("");
  const [positions, setPositions] = useState([]);
  const [kotRound, setKotRound] = useState("");
  const [cameraView, setCameraView] = useState("spectator");

  const busy = saving || deleting;
  const preferredTableFromVideo = useMemo(
    () => parseTableNumberFromVideoUrl(message?.video_message_url),
    [message?.video_message_url]
  );
  const preferredTableFromSetup = useMemo(
    () => parseViewSetupTableNumber(message),
    [message]
  );
  const preferredTable =
    preferredTableFromSetup != null
      ? preferredTableFromSetup
      : preferredTableFromVideo;
  const messageKotMatchId = useMemo(
    () => parseKotMatchIdFromFeedbackText(message?.text_message),
    [message?.text_message]
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDeleting(false);
    setTeId(String(message?.te_id || currentTeId || "").trim());
    const seq =
      message?.sequence_number ??
      message?.inferred_sequence_number ??
      "";
    setSequenceNumber(seq === null || seq === undefined || seq === "" ? "" : String(seq));
    setTableNumber(preferredTable != null ? String(preferredTable) : "");
    setPosition("");
    setPositions([]);
    setKotRound("");
    setCameraView(cameraViewFromMessage(message));
  }, [open, message, currentTeId, preferredTable]);

  useEffect(() => {
    if (!open || !tsId) return;
    let cancelled = false;
    async function loadTargets() {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(`${ip_back}api/mobileAPI/sessionMoveVideoTargets`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ ts_id: tsId }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || json.status !== "success") {
          throw new Error(json.message || t("moveVideoLoadFailed", "Failed to load move targets."));
        }
        if (!cancelled) {
          setExercises(Array.isArray(json.data?.exercises) ? json.data.exercises : []);
        }
      } catch (err) {
        if (!cancelled) {
          setExercises([]);
          setError(err.message || t("moveVideoLoadFailed", "Failed to load move targets."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadTargets();
    return () => {
      cancelled = true;
    };
  }, [open, tsId, t]);

  const selectedExercise = useMemo(
    () => exercises.find((ex) => String(ex.te_id) === String(teId)) || null,
    [exercises, teId]
  );

  const isKotMode = Boolean(selectedExercise?.is_kot_type);
  const kotMatches = selectedExercise?.kot_matches || [];

  const kotRounds = useMemo(() => {
    const set = new Set();
    for (const m of kotMatches) {
      const n = Number(m.round_number);
      if (Number.isFinite(n) && n > 0) set.add(n);
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [kotMatches]);

  const kotTablesForRound = useMemo(() => {
    const round = Number(kotRound);
    const set = new Set();
    for (const m of kotMatches) {
      if (Number(m.round_number) !== round) continue;
      const n = Number(m.table_number);
      if (Number.isFinite(n) && n > 0) set.add(n);
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [kotMatches, kotRound]);

  const selectedKotMatch = useMemo(() => {
    if (!isKotMode) return null;
    const round = Number(kotRound);
    const table = Number(tableNumber);
    if (!Number.isFinite(round) || !Number.isFinite(table)) return null;
    return (
      kotMatches.find(
        (m) =>
          Number(m.round_number) === round && Number(m.table_number) === table
      ) || null
    );
  }, [isKotMode, kotMatches, kotRound, tableNumber]);

  // Prefill KoT round/table/players from the clip's kot_match_id once targets load.
  useEffect(() => {
    if (!open || !isKotMode || !kotMatches.length) return;
    if (!messageKotMatchId) return;
    const match = kotMatches.find(
      (m) => String(m.kot_match_id) === String(messageKotMatchId)
    );
    if (!match) return;
    if (match.round_number != null) setKotRound(String(match.round_number));
    if (match.table_number != null) setTableNumber(String(match.table_number));
    const senderId = message?.sender_id != null ? String(message.sender_id) : null;
    const selected = [];
    if (
      senderId &&
      match.player_a?.user_id &&
      String(match.player_a.user_id) === senderId
    ) {
      selected.push("A");
    }
    if (
      senderId &&
      match.player_b?.user_id &&
      String(match.player_b.user_id) === senderId
    ) {
      selected.push("B");
    }
    // Default both players — KoT match clip is typically shared.
    setPositions(selected.length ? selected : ["A", "B"]);
  }, [open, isKotMode, kotMatches, messageKotMatchId, message?.sender_id]);

  // When switching into KoT mode without a known match, pick first round/table.
  useEffect(() => {
    if (!open || !isKotMode) return;
    if (!kotRounds.length) {
      setKotRound("");
      return;
    }
    const current = Number(kotRound);
    if (Number.isFinite(current) && kotRounds.includes(current)) return;
    setKotRound(String(kotRounds[0]));
  }, [open, isKotMode, kotRounds, kotRound]);

  useEffect(() => {
    if (!open || !isKotMode) return;
    if (!kotTablesForRound.length) {
      if (tableNumber) setTableNumber("");
      return;
    }
    const current = Number(tableNumber);
    if (Number.isFinite(current) && kotTablesForRound.includes(current)) return;
    setTableNumber(String(kotTablesForRound[0]));
  }, [open, isKotMode, kotTablesForRound, tableNumber]);

  // When KoT match changes, default both players selected.
  useEffect(() => {
    if (!open || !isKotMode || !selectedKotMatch) return;
    if (positions.length) {
      const allowed = new Set(["A", "B"]);
      const valid = positions.filter((p) => allowed.has(p));
      if (valid.length !== positions.length) setPositions(valid);
      if (valid.length) return;
    }
    setPositions(["A", "B"]);
  }, [open, isKotMode, selectedKotMatch, positions]);

  const availableTables = useMemo(
    () => tableOptionsForExercise(selectedExercise),
    [selectedExercise]
  );

  // Keep / restore a sensible table when exercise changes (sequence mode).
  useEffect(() => {
    if (!open || !selectedExercise || isKotMode) return;
    if (!availableTables.length) {
      setTableNumber("");
      return;
    }
    const current = Number(tableNumber);
    if (Number.isFinite(current) && availableTables.includes(current)) return;

    const preferred = Number(preferredTable);
    if (Number.isFinite(preferred) && availableTables.includes(preferred)) {
      setTableNumber(String(preferred));
      return;
    }
    setTableNumber(String(availableTables[0]));
  }, [
    open,
    selectedExercise,
    availableTables,
    tableNumber,
    preferredTable,
    isKotMode,
  ]);

  const selectedTable = Number(tableNumber);
  const hasSelectedTable = Number.isFinite(selectedTable) && selectedTable > 0;

  const allSequences = selectedExercise?.sequences || [];
  const sequences = useMemo(() => {
    if (isKotMode) return [];
    if (!hasSelectedTable) return allSequences;
    return allSequences.filter((s) => {
      const slotsOnTable = (s.slots || []).some(
        (slot) => Number(slot.table_number) === selectedTable
      );
      if (slotsOnTable) return true;
      return Number(s.table_number) === selectedTable;
    });
  }, [allSequences, hasSelectedTable, selectedTable, isKotMode]);

  const selectedSequence = useMemo(() => {
    if (isKotMode || !sequences.length) return null;
    if (sequenceNumber === "" || sequenceNumber == null) {
      return sequences.find((s) => s.sequence_order == null) || sequences[0] || null;
    }
    return (
      sequences.find((s) => Number(s.sequence_order) === Number(sequenceNumber)) ||
      null
    );
  }, [sequences, sequenceNumber, isKotMode]);

  useEffect(() => {
    if (!open || isKotMode) return;
    if (!sequences.length) {
      if (sequenceNumber !== "") setSequenceNumber("");
      return;
    }
    if (
      sequenceNumber !== "" &&
      sequences.some((s) => String(s.sequence_order) === String(sequenceNumber))
    ) {
      return;
    }
    const firstOrdered = sequences.find((s) => s.sequence_order != null);
    setSequenceNumber(
      firstOrdered?.sequence_order != null
        ? String(firstOrdered.sequence_order)
        : ""
    );
  }, [open, sequences, sequenceNumber, isKotMode]);

  const slots = useMemo(() => {
    if (isKotMode) return [];
    const raw = selectedSequence?.slots || [];
    if (!hasSelectedTable) return raw;
    return raw.filter((s) => {
      const n = Number(s.table_number);
      // Keep unscoped roster slots (common on warmup TE_ONCE fallbacks).
      if (!Number.isFinite(n) || n <= 0) return true;
      return n === selectedTable;
    });
  }, [selectedSequence, hasSelectedTable, selectedTable, isKotMode]);

  const activeSlots = useMemo(
    () => slots.filter((s) => isActiveSlotRole(s.role)),
    [slots]
  );
  const inclusiveActiveSelection = !isKotMode && activeSlots.length >= 2;

  useEffect(() => {
    if (!open || isKotMode) return;
    if (!slots.length) {
      if (position) setPosition("");
      if (positions.length) setPositions([]);
      return;
    }

    if (inclusiveActiveSelection) {
      const allowed = new Set(activeSlots.map((s) => s.position));
      const currentValid = positions.filter((p) => allowed.has(p));
      if (currentValid.length) {
        if (currentValid.length !== positions.length) setPositions(currentValid);
        return;
      }
      setPositions(activeSlots.map((s) => s.position));
      setPosition("");
      return;
    }

    if (positions.length) setPositions([]);
    if (position && slots.some((s) => s.position === position)) return;
    const senderId = message?.sender_id != null ? String(message.sender_id) : null;
    const bySender = senderId
      ? slots.find((s) => String(s.user_id) === senderId)
      : null;
    setPosition(bySender?.position || slots[0]?.position || "");
  }, [
    open,
    isKotMode,
    slots,
    activeSlots,
    inclusiveActiveSelection,
    position,
    positions,
    message?.sender_id,
  ]);

  const canSubmitKot =
    isKotMode &&
    teId &&
    selectedKotMatch &&
    positions.length > 0 &&
    positions.every((p) => p === "A" || p === "B");

  const canSubmitSequence =
    !isKotMode &&
    teId &&
    (inclusiveActiveSelection
      ? positions.length > 0 &&
        positions.every((p) => slots.some((s) => s.position === p))
      : position && slots.some((s) => s.position === position)) &&
    (availableTables.length === 0 || hasSelectedTable) &&
    (sequences.every((s) => s.sequence_order == null) ||
      (sequenceNumber !== "" && selectedSequence));

  const canSubmit = !loading && !busy && (canSubmitKot || canSubmitSequence);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message?.message_id) {
      setError(t("moveVideoFailed", "Failed to move video."));
      return;
    }
    if (!canSubmit) {
      setError(
        isKotMode
          ? t(
              "moveVideoIncompleteKot",
              "Select round, table, and at least one match player before saving."
            )
          : t(
              "moveVideoIncomplete",
              "Select sequence, table, and court side before saving."
            )
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        message_id: message.message_id,
        te_id: teId,
        camera_view: cameraView === "referee" ? "referee" : "spectator",
      };

      if (isKotMode) {
        body.kot_match_id = selectedKotMatch.kot_match_id;
        body.positions = positions;
        body.position = positions[0];
        if (hasSelectedTable) body.table_number = selectedTable;
      } else {
        if (inclusiveActiveSelection) {
          body.positions = positions;
          body.position = positions[0];
        } else {
          body.position = position;
        }
        if (hasSelectedTable) body.table_number = selectedTable;
        if (
          selectedSequence &&
          selectedSequence.sequence_order != null &&
          Number(selectedSequence.sequence_order) > 0
        ) {
          body.sequence_number = Number(selectedSequence.sequence_order);
        } else {
          body.sequence_number = null;
        }
      }

      const resp = await fetch(`${ip_back}api/mobileAPI/moveEvaluationVideo`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.status !== "success") {
        throw new Error(json.message || t("moveVideoFailed", "Failed to move video."));
      }
      onMoved?.(json.data);
      onClose?.();
    } catch (err) {
      setError(err.message || t("moveVideoFailed", "Failed to move video."));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!message?.message_id || busy) return;
    const confirmed = window.confirm(
      t(
        "deleteEvaluationVideoConfirm",
        "Delete this evaluation video permanently? Use this for duplicate uploads. This cannot be undone."
      )
    );
    if (!confirmed) return;

    setDeleting(true);
    setError(null);
    try {
      const resp = await fetch(`${ip_back}api/mobileAPI/deleteEvaluationVideo`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ message_id: message.message_id }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.status !== "success") {
        throw new Error(
          json.message || t("deleteEvaluationVideoFailed", "Failed to delete video.")
        );
      }
      onDeleted?.(json.data);
      onClose?.();
    } catch (err) {
      setError(
        err.message || t("deleteEvaluationVideoFailed", "Failed to delete video.")
      );
    } finally {
      setDeleting(false);
    }
  }

  function resetAssignmentFields() {
    setSequenceNumber("");
    setPosition("");
    setPositions([]);
    setKotRound("");
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      className="move-eval-video-dialog-root"
      sx={{ zIndex }}
    >
      <DialogContent className="move-eval-video-dialog">
        <h3 className="move-eval-video-dialog__title">
          {t("moveVideoUpdateParams", "Update video parameters")}
        </h3>
        <p className="move-eval-video-dialog__subtitle">
          {isKotMode
            ? t(
                "moveKotVideoHint",
                "Update exercise, round, table, match players, and camera type for this KoT clip."
              )
            : t(
                "moveEvaluationVideoHint",
                "Update exercise, sequence, table, court side (A–D), and camera type (spectator or referee) for this clip."
              )}
        </p>

        {loading ? (
          <div className="move-eval-video-dialog__loading">
            <CircularProgress size={28} style={{ color: "#0A84FF" }} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="move-eval-video-dialog__form">
            <label className="move-eval-video-dialog__label">
              {t("exercise", "Exercise")}
              <select
                value={teId}
                onChange={(ev) => {
                  setTeId(ev.target.value);
                  resetAssignmentFields();
                }}
                required
                disabled={busy}
              >
                <option value="">{t("selectExercise", "Select exercise")}</option>
                {exercises.map((ex) => (
                  <option key={ex.te_id} value={ex.te_id}>
                    {formatExerciseOption(ex, t, {
                      multiStation:
                        new Set(
                          exercises.map((row) => row.tsg_id).filter(Boolean)
                        ).size > 1,
                    })}
                  </option>
                ))}
              </select>
            </label>

            {isKotMode ? (
              <>
                <label className="move-eval-video-dialog__label">
                  {t("round", "Round")}
                  <select
                    value={kotRound}
                    onChange={(ev) => {
                      setKotRound(ev.target.value);
                      setTableNumber("");
                      setPositions([]);
                    }}
                    required
                    disabled={busy || !kotRounds.length}
                  >
                    <option value="">
                      {kotRounds.length
                        ? t("selectRound", "Select round")
                        : t("noRoundsAvailable", "No rounds available")}
                    </option>
                    {kotRounds.map((n) => (
                      <option key={n} value={String(n)}>
                        {t("round_with_number", {
                          number: n,
                          defaultValue: `Round ${n}`,
                        })}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="move-eval-video-dialog__label">
                  {t("table", "Table")}
                  <select
                    value={tableNumber}
                    onChange={(ev) => {
                      setTableNumber(ev.target.value);
                      setPositions([]);
                    }}
                    required
                    disabled={busy || !kotTablesForRound.length}
                  >
                    <option value="">
                      {kotTablesForRound.length
                        ? t("selectTable", "Select table")
                        : t("noTablesAvailable", "No tables available")}
                    </option>
                    {kotTablesForRound.map((n) => (
                      <option key={n} value={String(n)}>
                        {t("table_with_number", {
                          number: n,
                          defaultValue: `Table ${n}`,
                        })}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="move-eval-video-dialog__label">
                  {t("matchPlayers", "Match players")}
                  <div className="move-eval-video-dialog__multi">
                    {!selectedKotMatch ? (
                      <p className="move-eval-video-dialog__hint">
                        {t(
                          "selectRoundAndTableForMatch",
                          "Select a round and table to see the match players."
                        )}
                      </p>
                    ) : (
                      <>
                        <p className="move-eval-video-dialog__hint">
                          {t(
                            "moveKotPlayersHint",
                            "Select which player(s) this clip should be linked to."
                          )}
                        </p>
                        {["A", "B"].map((side) => {
                          const player =
                            side === "A"
                              ? selectedKotMatch.player_a
                              : selectedKotMatch.player_b;
                          const checked = positions.includes(side);
                          return (
                            <label
                              key={side}
                              className="move-eval-video-dialog__check"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={busy || !player}
                                onChange={() => {
                                  setPositions((prev) =>
                                    checked
                                      ? prev.filter((p) => p !== side)
                                      : [...prev, side]
                                  );
                                }}
                              />
                              <span>{formatKotPlayerLabel(player, side, t)}</span>
                            </label>
                          );
                        })}
                      </>
                    )}
                  </div>
                </label>
              </>
            ) : (
              <>
                <label className="move-eval-video-dialog__label">
                  {t("table", "Table")}
                  <select
                    value={tableNumber}
                    onChange={(ev) => {
                      setTableNumber(ev.target.value);
                      setPosition("");
                      setPositions([]);
                    }}
                    required={availableTables.length > 0}
                    disabled={busy || !availableTables.length}
                  >
                    <option value="">
                      {availableTables.length
                        ? t("selectTable", "Select table")
                        : t("noTablesAvailable", "No tables available")}
                    </option>
                    {availableTables.map((n) => (
                      <option key={n} value={String(n)}>
                        {t("table_with_number", {
                          number: n,
                          defaultValue: `Table ${n}`,
                        })}
                      </option>
                    ))}
                  </select>
                </label>

                {preferredTableFromVideo != null &&
                  availableTables.length > 0 &&
                  !availableTables.includes(Number(preferredTableFromVideo)) && (
                    <p className="move-eval-video-dialog__hint">
                      {t(
                        "moveVideoTableMismatchHint",
                        "This clip is from Table {{videoTable}}. Pick the exercise that includes that table, or choose another table below.",
                        { videoTable: preferredTableFromVideo }
                      )}
                    </p>
                  )}

                {sequences.some((s) => s.sequence_order != null) && (
                  <label className="move-eval-video-dialog__label">
                    {t("sequence", "Sequence")}
                    <select
                      value={sequenceNumber}
                      onChange={(ev) => {
                        setSequenceNumber(ev.target.value);
                        setPosition("");
                        setPositions([]);
                      }}
                      required
                      disabled={busy}
                    >
                      <option value="">{t("selectSequence", "Select sequence")}</option>
                      {sequences
                        .filter((s) => s.sequence_order != null)
                        .map((s) => (
                          <option
                            key={s.tes_id || s.sequence_order}
                            value={String(s.sequence_order)}
                          >
                            {formatSequenceOption(s, t, localeCode)}
                          </option>
                        ))}
                    </select>
                  </label>
                )}

                <label className="move-eval-video-dialog__label">
                  {t("playerPosition", "Court side")}
                  {inclusiveActiveSelection ? (
                    <div className="move-eval-video-dialog__multi">
                      <p className="move-eval-video-dialog__hint">
                        {t(
                          "moveVideoDualActiveHint",
                          "Both players are Active — select everyone who should receive this clip."
                        )}
                      </p>
                      {activeSlots.map((slot) => {
                        const checked = positions.includes(slot.position);
                        return (
                          <label
                            key={`${slot.position}-${slot.player_id || slot.user_id}`}
                            className="move-eval-video-dialog__check"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={busy}
                              onChange={() => {
                                setPositions((prev) =>
                                  checked
                                    ? prev.filter((p) => p !== slot.position)
                                    : [...prev, slot.position]
                                );
                              }}
                            />
                            <span>{formatSlotOptionLabel(slot, t)}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <select
                      value={position}
                      onChange={(ev) => setPosition(ev.target.value)}
                      required
                      disabled={!slots.length || busy}
                    >
                      <option value="">
                        {slots.length
                          ? t("selectPlayerPosition", "Select side (A–D)")
                          : t(
                              "noSlotsAvailable",
                              "No players on this table / sequence"
                            )}
                      </option>
                      {slots.map((slot) => (
                        <option
                          key={`${slot.position}-${slot.player_id || slot.user_id}`}
                          value={slot.position}
                        >
                          {formatSlotOptionLabel(slot, t)}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              </>
            )}

            <label className="move-eval-video-dialog__label">
              {t("cameraType", "Camera type")}
              <div
                className="move-eval-video-dialog__segment"
                role="radiogroup"
                aria-label={t("cameraType", "Camera type")}
              >
                {[
                  ["spectator", t("cameraTypeSpectator", "Spectator")],
                  ["referee", t("cameraTypeReferee", "Referee")],
                ].map(([value, label]) => {
                  const selected = cameraView === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={[
                        "move-eval-video-dialog__segment-btn",
                        selected
                          ? "move-eval-video-dialog__segment-btn--active"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={busy}
                      onClick={() => setCameraView(value)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="move-eval-video-dialog__hint move-eval-video-dialog__hint--muted">
                {t(
                  "cameraTypeMoveHint",
                  "Spectator is behind a table end. Referee is on the long side, looking across the net."
                )}
              </p>
            </label>

            {error && <div className="move-eval-video-dialog__error">{error}</div>}

            <div className="move-eval-video-dialog__actions">
              <button
                type="button"
                className="move-eval-video-dialog__delete-btn"
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  handleDelete();
                }}
                disabled={busy || !message?.message_id}
                aria-label={t("deleteEvaluationVideo", "Delete video")}
                title={t(
                  "deleteEvaluationVideoHint",
                  "Permanently delete this video (e.g. duplicate upload)"
                )}
              >
                {deleting ? (
                  <CircularProgress size={18} style={{ color: "#c62828" }} />
                ) : (
                  <DeleteOutlineIcon style={{ fontSize: 22 }} />
                )}
              </button>
              <div className="move-eval-video-dialog__actions-right">
                <button
                  type="button"
                  className="move-eval-video-dialog__btn move-eval-video-dialog__btn--secondary"
                  onClick={onClose}
                  disabled={busy}
                >
                  {t("cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  className="move-eval-video-dialog__btn move-eval-video-dialog__btn--primary"
                  disabled={busy || loading}
                >
                  {saving
                    ? t("savingVideoParams", "Saving…")
                    : t("saveVideoParams", "Save parameters")}
                </button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MoveEvaluationVideoDialog;
